import { useCallback, useEffect, useState } from 'react';
import { isNil } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import {
  ApiService,
  CalibrationDependency,
  RecalculatePublicCompsDependenciesRequest,
  RecalculatePublicCompsDependenciesResponse,
  ValuationApproachDependency,
} from 'api';
import { ValuationsApproach } from 'common/types/valuation';
import { type RegistrationAPI } from 'pages/ValuationsAllocation/hooks/useApproachDependencyGraph';
import { type StableIdGenerator } from 'pages/ValuationsAllocation/hooks/useApproachStableIds';
import { buildCalibrationPanelId } from 'pages/ValuationsAllocation/util';
import { dbShortDate } from 'utilities';
import validateRecalculationInputs from './validateRecalculationInputs';

const useDependenciesRecalculation = ({
  stableIds,
  dependencyGraph,
  approaches,
  calibrations,
  valuationId,
  marketDate,
}: {
  stableIds: StableIdGenerator;
  approaches: ValuationApproachDependency[];
  dependencyGraph: RegistrationAPI;
  calibrations: CalibrationDependency[];
  valuationId?: number;
  marketDate: string;
}) => {
  const [isRecalculating, setIsRecalculating] = useState(false);
  // a recalculation starts when this is set to a value (stable id from dependency graph). When finalizing the recalculation switch this to null
  const [sourceOfChange, setSourceOfChange] = useState<string | null>(null);
  const [recalculatedState, setRecalculatedState] = useState<RecalculatePublicCompsDependenciesResponse[] | null>(null);

  const makeRecalculateRequest = useCallback(async (statePayload: RecalculatePublicCompsDependenciesRequest) => {
    setIsRecalculating(true);
    try {
      enqueueSnackbar('Recalculating dependent approaches...', { variant: 'info' });
      const response = await ApiService.apiValuationPublicCompsRecalculateDependenciesCreate(statePayload);
      setRecalculatedState(response);
    } catch (error) {
      console.error('Error during recalculation:', error);
      setRecalculatedState(null);
    } finally {
      setIsRecalculating(false);
      setSourceOfChange(null);
    }
  }, []);

  useEffect(() => {
    const startRecalculation = async (
      publicCompsApproach: ValuationApproachDependency,
      dependencies: ValuationApproachDependency[],
      valuationId: number,
      calibrationsDependencies: CalibrationDependency[]
    ) => {
      await makeRecalculateRequest({
        public_comps_state: publicCompsApproach.valuations_approach_gpc!,
        approaches_dependencies: dependencies,
        calibrations_dependencies: calibrationsDependencies, // for now we only support recalculating dependencies related to public comps, so this will be empty
        valuation_id: valuationId,
        market_date: dbShortDate(marketDate) ?? marketDate,
      });
    };

    const dependentNodes = sourceOfChange ? dependencyGraph.getDependents(sourceOfChange) : [];
    if (
      valuationId &&
      sourceOfChange &&
      !isRecalculating &&
      dependentNodes.length &&
      (approaches.length || calibrations.length)
    ) {
      const dependenciesPanelIds = new Set(dependentNodes.map(node => node.panelId));
      const affectedApproaches = approaches.filter(approach => dependenciesPanelIds.has(approach.panel_id as string));
      const affectedCalibrations = calibrations.filter(calibration =>
        dependenciesPanelIds.has(buildCalibrationPanelId(calibration.panel_id as string))
      );
      const sourceOfTruthNode = dependencyGraph.readNode(sourceOfChange);
      const sourceOfTruthApproach = approaches.find(approach => approach.panel_id === sourceOfTruthNode!.panelId);
      const validAffectedApproaches = validateRecalculationInputs({
        sourceOfTruthApproach: sourceOfTruthApproach!,
        affectedApproaches,
      });
      if (validAffectedApproaches.length || affectedCalibrations.length) {
        startRecalculation(sourceOfTruthApproach!, validAffectedApproaches, valuationId, affectedCalibrations);
      } else {
        setSourceOfChange(null);
      }
    }
  }, [
    sourceOfChange,
    dependencyGraph,
    approaches,
    makeRecalculateRequest,
    stableIds,
    valuationId,
    marketDate,
    isRecalculating,
    calibrations,
    setSourceOfChange,
  ]);

  const applyRecalculationToApproaches = useCallback(
    (
      currentApproaches: ValuationsApproach[],
      recalculatedState: RecalculatePublicCompsDependenciesResponse[] | null
    ) => {
      if (!(recalculatedState ?? []).length) {
        return currentApproaches;
      }

      const updatesByPanelId = new Map<string, ValuationsApproach>();
      const updatesById = new Map<number, ValuationsApproach>();

      recalculatedState!
        .filter(({ status, approach }) => status === 'success' && approach.approach_type)
        .forEach(newState => {
          const responseApproach = newState?.approach as unknown as ValuationsApproach;

          const panelId = newState.panel_id;
          const normalizedApproach = {
            ...responseApproach,
            panelId: panelId ?? responseApproach.panelId,
          } as ValuationsApproach;

          if (panelId) {
            updatesByPanelId.set(panelId, normalizedApproach);
          }

          if (!isNil(normalizedApproach.id)) {
            updatesById.set(normalizedApproach.id, normalizedApproach);
          }
        });

      if (!updatesByPanelId.size && !updatesById.size) {
        return currentApproaches;
      }

      let didUpdate = false;
      const mergedApproaches = currentApproaches.map(approach => {
        const update =
          (approach.panelId && updatesByPanelId.get(approach.panelId)) ||
          (isNil(approach.id) ? undefined : updatesById.get(approach.id));

        if (!update) {
          return approach;
        }

        didUpdate = true;
        return {
          ...approach,
          ...update,
          panelId: approach.panelId ?? update.panelId,
        };
      });
      return didUpdate ? mergedApproaches : currentApproaches;
    },
    []
  );

  const applyRecalculationToCalibrations = useCallback(
    (
      currentCalibrations: CalibrationDependency[],
      recalculatedState: RecalculatePublicCompsDependenciesResponse[] | null
    ) => {
      if (!(recalculatedState ?? []).length) {
        return currentCalibrations;
      }
      const updatesByPanelId = new Map<string, CalibrationDependency>();
      const updatesById = new Map<number, CalibrationDependency>();

      recalculatedState!
        .filter(({ status, approach }) => status === 'success' && isNil(approach.approach_type))
        .forEach(newState => {
          const responseCalibration = newState?.approach as unknown as CalibrationDependency;
          const panelId = newState.panel_id;

          if (panelId) {
            updatesByPanelId.set(panelId, responseCalibration);
          }
          if (!isNil(responseCalibration.id)) {
            updatesById.set(responseCalibration.id, responseCalibration);
          }
        });

      if (!updatesByPanelId.size && !updatesById.size) {
        return currentCalibrations;
      }

      const mergedCalibrations = currentCalibrations.map(calibration => {
        const update =
          (calibration.panel_id && updatesByPanelId.get(calibration.panel_id)) ||
          (isNil(calibration.id) ? undefined : updatesById.get(calibration.id));

        return update ?? calibration;
      });
      return mergedCalibrations;
    },
    []
  );

  return {
    recalculatedState,
    isRecalculating,
    makeRecalculateRequest,
    setSourceOfChange,
    setRecalculatedState,
    applyRecalculationToApproaches,
    applyRecalculationToCalibrations,
  };
};

export default useDependenciesRecalculation;
